import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  Plus, 
  Trash2, 
  Edit2, 
  Save, 
  X, 
  Mail, 
  Briefcase, 
  Settings, 
  Image, 
  Share2, 
  FileText,
  MessageSquare,
  Eye,
  EyeOff
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [activeTab, setActiveTab] = useState("about");
  const [loading, setLoading] = useState(false);

  // Data states
  const [aboutContent, setAboutContent] = useState(null);
  const [services, setServices] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [socialLinks, setSocialLinks] = useState([]);
  const [contactMessages, setContactMessages] = useState([]);

  // Edit states
  const [editingItem, setEditingItem] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      if (res.data.success) {
        setIsAuthenticated(true);
        sessionStorage.setItem("adminToken", res.data.token);
        toast.success("Login erfolgreich!");
      } else {
        toast.error("Falsches Passwort");
      }
    } catch (error) {
      toast.error("Login fehlgeschlagen");
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [aboutRes, servicesRes, portfolioRes, jobsRes, socialRes, contactRes] = await Promise.all([
        axios.get(`${API}/admin/about`),
        axios.get(`${API}/admin/services`),
        axios.get(`${API}/admin/portfolio`),
        axios.get(`${API}/admin/jobs`),
        axios.get(`${API}/admin/social`),
        axios.get(`${API}/contact`)
      ]);
      setAboutContent(aboutRes.data);
      setServices(servicesRes.data);
      setPortfolio(portfolioRes.data);
      setJobs(jobsRes.data);
      setSocialLinks(socialRes.data);
      setContactMessages(contactRes.data);
    } catch (error) {
      
    }
    setLoading(false);
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const token = sessionStorage.getItem("adminToken");
    if (token) {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogout = () => {
    sessionStorage.removeItem("adminToken");
    setIsAuthenticated(false);
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <a href="/" className="inline-flex items-center gap-2 text-mmg-silver-chalice hover:text-white mb-8 transition-colors">
            <ArrowLeft size={20} />
            <span className="font-heading text-sm uppercase tracking-widest">Zurück zur Website</span>
          </a>
          
          <h1 className="font-heading font-bold text-4xl text-white mb-8">ADMIN LOGIN</h1>
          
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Passwort"
                className="w-full py-4 px-0 font-body text-base text-white bg-transparent border-b border-mmg-mine-shaft focus:border-white outline-none transition-colors placeholder:text-mmg-silver-chalice"
              />
            </div>
            <button
              type="submit"
              className="w-full border border-white bg-white text-black py-4 font-heading font-bold uppercase tracking-widest hover:bg-black hover:text-white transition-all duration-300"
            >
              Anmelden
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Tab Navigation
  const tabs = [
    { id: "about", label: "About", icon: FileText },
    { id: "services", label: "Services", icon: Settings },
    { id: "portfolio", label: "Portfolio", icon: Image },
    { id: "jobs", label: "Jobs", icon: Briefcase },
    { id: "social", label: "Social Media", icon: Share2 },
    { id: "contact", label: "Kontaktanfragen", icon: MessageSquare }
  ];

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="border-b border-mmg-mine-shaft px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-6">
            <a href="/" className="font-heading font-bold text-xl text-white">
              MAHZOD ADMIN
            </a>
          </div>
          <button
            onClick={handleLogout}
            className="font-heading text-sm uppercase tracking-widest text-mmg-silver-chalice hover:text-white transition-colors"
          >
            Abmelden
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8 border-b border-mmg-mine-shaft pb-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setShowForm(false); setEditingItem(null); }}
              className={`flex items-center gap-2 px-4 py-2 font-heading text-sm uppercase tracking-widest transition-colors ${
                activeTab === tab.id 
                  ? "bg-white text-black" 
                  : "text-mmg-silver-chalice hover:text-white"
              }`}
            >
              <tab.icon size={16} />
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-mmg-silver-chalice">Laden...</p>
          </div>
        ) : (
          <>
            {/* About Tab */}
            {activeTab === "about" && (
              <AboutTab 
                content={aboutContent} 
                onUpdate={(updated) => setAboutContent(updated)}
              />
            )}

            {/* Services Tab */}
            {activeTab === "services" && (
              <ServicesTab 
                services={services}
                onUpdate={() => fetchData()}
              />
            )}

            {/* Portfolio Tab */}
            {activeTab === "portfolio" && (
              <PortfolioTab 
                portfolio={portfolio}
                onUpdate={() => fetchData()}
              />
            )}

            {/* Jobs Tab */}
            {activeTab === "jobs" && (
              <JobsTab 
                jobs={jobs}
                onUpdate={() => fetchData()}
              />
            )}

            {/* Social Media Tab */}
            {activeTab === "social" && (
              <SocialTab 
                links={socialLinks}
                onUpdate={() => fetchData()}
              />
            )}

            {/* Contact Messages Tab */}
            {activeTab === "contact" && (
              <ContactTab 
                messages={contactMessages}
                onUpdate={() => fetchData()}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

// About Tab Component
const AboutTab = ({ content, onUpdate }) => {
  const [formData, setFormData] = useState(content || {});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (content) setFormData(content);
  }, [content]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/about`, formData);
      onUpdate(res.data);
      toast.success("About-Inhalt gespeichert!");
    } catch (error) {
      toast.error("Fehler beim Speichern");
    }
    setSaving(false);
  };

  return (
    <div className="space-y-8">
      <h2 className="font-heading font-bold text-2xl text-white">About / Mission bearbeiten</h2>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* English */}
        <div className="space-y-4">
          <h3 className="font-heading text-lg text-white">English</h3>
          <input
            type="text"
            value={formData.title_en || ""}
            onChange={(e) => setFormData({...formData, title_en: e.target.value})}
            placeholder="Title"
            className="w-full py-3 px-4 bg-mmg-cod-gray border border-mmg-mine-shaft text-white font-body"
          />
          <textarea
            value={formData.description_en || ""}
            onChange={(e) => setFormData({...formData, description_en: e.target.value})}
            placeholder="Description"
            rows={6}
            className="w-full py-3 px-4 bg-mmg-cod-gray border border-mmg-mine-shaft text-white font-body resize-none"
          />
        </div>

        {/* German */}
        <div className="space-y-4">
          <h3 className="font-heading text-lg text-white">Deutsch</h3>
          <input
            type="text"
            value={formData.title_de || ""}
            onChange={(e) => setFormData({...formData, title_de: e.target.value})}
            placeholder="Titel"
            className="w-full py-3 px-4 bg-mmg-cod-gray border border-mmg-mine-shaft text-white font-body"
          />
          <textarea
            value={formData.description_de || ""}
            onChange={(e) => setFormData({...formData, description_de: e.target.value})}
            placeholder="Beschreibung"
            rows={6}
            className="w-full py-3 px-4 bg-mmg-cod-gray border border-mmg-mine-shaft text-white font-body resize-none"
          />
        </div>

        {/* Arabic */}
        <div className="space-y-4">
          <h3 className="font-heading text-lg text-white">العربية</h3>
          <input
            type="text"
            value={formData.title_ar || ""}
            onChange={(e) => setFormData({...formData, title_ar: e.target.value})}
            placeholder="العنوان"
            dir="rtl"
            className="w-full py-3 px-4 bg-mmg-cod-gray border border-mmg-mine-shaft text-white font-body"
          />
          <textarea
            value={formData.description_ar || ""}
            onChange={(e) => setFormData({...formData, description_ar: e.target.value})}
            placeholder="الوصف"
            dir="rtl"
            rows={6}
            className="w-full py-3 px-4 bg-mmg-cod-gray border border-mmg-mine-shaft text-white font-body resize-none"
          />
        </div>
      </div>

      <button
        onClick={handleSave}
        disabled={saving}
        className="inline-flex items-center gap-2 border border-white bg-white text-black px-6 py-3 font-heading font-bold uppercase tracking-widest hover:bg-black hover:text-white transition-all duration-300 disabled:opacity-50"
      >
        <Save size={18} />
        {saving ? "Speichern..." : "Speichern"}
      </button>
    </div>
  );
};

// Services Tab Component
const ServicesTab = ({ services, onUpdate }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    title_en: "", title_de: "", title_ar: "",
    desc_en: "", desc_de: "", desc_ar: "",
    icon: "Settings"
  });

  const iconOptions = ["ShoppingCart", "Package", "Truck", "Palette", "Cpu", "Settings", "Globe", "Zap", "Target", "TrendingUp"];

  const handleSave = async () => {
    try {
      if (editingItem) {
        await axios.put(`${API}/admin/services/${editingItem.id}`, formData);
        toast.success("Service aktualisiert!");
      } else {
        await axios.post(`${API}/admin/services`, formData);
        toast.success("Service erstellt!");
      }
      setShowForm(false);
      setEditingItem(null);
      setFormData({ title_en: "", title_de: "", title_ar: "", desc_en: "", desc_de: "", desc_ar: "", icon: "Settings" });
      onUpdate();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Wirklich löschen?")) {
      try {
        await axios.delete(`${API}/admin/services/${id}`);
        toast.success("Service gelöscht!");
        onUpdate();
      } catch (error) {
        toast.error("Fehler beim Löschen");
      }
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData(item);
    setShowForm(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-heading font-bold text-2xl text-white">Services verwalten</h2>
        <button
          onClick={() => { setShowForm(true); setEditingItem(null); setFormData({ title_en: "", title_de: "", title_ar: "", desc_en: "", desc_de: "", desc_ar: "", icon: "Settings" }); }}
          className="inline-flex items-center gap-2 border border-white bg-white text-black px-4 py-2 font-heading font-bold text-sm uppercase tracking-widest hover:bg-black hover:text-white transition-all duration-300"
        >
          <Plus size={16} />
          Neu
        </button>
      </div>

      {showForm && (
        <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6 space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg text-white">{editingItem ? "Service bearbeiten" : "Neuer Service"}</h3>
            <button onClick={() => setShowForm(false)} className="text-mmg-silver-chalice hover:text-white">
              <X size={20} />
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <input type="text" value={formData.title_en} onChange={(e) => setFormData({...formData, title_en: e.target.value})} placeholder="Title (EN)" className="py-2 px-3 bg-black border border-mmg-mine-shaft text-white font-body" />
            <input type="text" value={formData.title_de} onChange={(e) => setFormData({...formData, title_de: e.target.value})} placeholder="Titel (DE)" className="py-2 px-3 bg-black border border-mmg-mine-shaft text-white font-body" />
            <input type="text" value={formData.title_ar} onChange={(e) => setFormData({...formData, title_ar: e.target.value})} placeholder="العنوان (AR)" dir="rtl" className="py-2 px-3 bg-black border border-mmg-mine-shaft text-white font-body" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <textarea value={formData.desc_en} onChange={(e) => setFormData({...formData, desc_en: e.target.value})} placeholder="Description (EN)" rows={3} className="py-2 px-3 bg-black border border-mmg-mine-shaft text-white font-body resize-none" />
            <textarea value={formData.desc_de} onChange={(e) => setFormData({...formData, desc_de: e.target.value})} placeholder="Beschreibung (DE)" rows={3} className="py-2 px-3 bg-black border border-mmg-mine-shaft text-white font-body resize-none" />
            <textarea value={formData.desc_ar} onChange={(e) => setFormData({...formData, desc_ar: e.target.value})} placeholder="الوصف (AR)" dir="rtl" rows={3} className="py-2 px-3 bg-black border border-mmg-mine-shaft text-white font-body resize-none" />
          </div>
          <div>
            <label className="font-heading text-sm text-mmg-silver-chalice block mb-2">Icon</label>
            <select value={formData.icon} onChange={(e) => setFormData({...formData, icon: e.target.value})} className="py-2 px-3 bg-black border border-mmg-mine-shaft text-white font-body">
              {iconOptions.map(icon => <option key={icon} value={icon}>{icon}</option>)}
            </select>
          </div>
          <button onClick={handleSave} className="inline-flex items-center gap-2 border border-white bg-white text-black px-4 py-2 font-heading font-bold text-sm uppercase tracking-widest hover:bg-black hover:text-white transition-all duration-300">
            <Save size={16} />
            Speichern
          </button>
        </div>
      )}

      <div className="space-y-2">
        {services.map((service) => (
          <div key={service.id} className="flex items-center justify-between bg-mmg-cod-gray border border-mmg-mine-shaft p-4 hover:border-mmg-boulder transition-colors">
            <div>
              <p className="font-heading text-white">{service.title_de || service.title_en}</p>
              <p className="font-body text-sm text-mmg-silver-chalice">{service.icon}</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => handleEdit(service)} className="p-2 text-mmg-silver-chalice hover:text-white"><Edit2 size={18} /></button>
              <button onClick={() => handleDelete(service.id)} className="p-2 text-mmg-silver-chalice hover:text-red-500"><Trash2 size={18} /></button>
            </div>
          </div>
        ))}
        {services.length === 0 && <p className="text-mmg-silver-chalice text-center py-8">Keine Services vorhanden</p>}
      </div>
    </div>
  );
};

// Portfolio Tab Component
const PortfolioTab = ({ portfolio, onUpdate }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: "", category_en: "", category_de: "", category_ar: "", logo: "", url: "#"
  });

  const handleSave = async () => {
    try {
      if (editingItem) {
        await axios.put(`${API}/admin/portfolio/${editingItem.id}`, formData);
        toast.success("Portfolio aktualisiert!");
      } else {
        await axios.post(`${API}/admin/portfolio`, formData);
        toast.success("Portfolio erstellt!");
      }
      setShowForm(false);
      setEditingItem(null);
      setFormData({ name: "", category_en: "", category_de: "", category_ar: "", logo: "", url: "#" });
      onUpdate();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Wirklich löschen?")) {
      try {
        await axios.delete(`${API}/admin/portfolio/${id}`);
        toast.success("Portfolio gelöscht!");
        onUpdate();
      } catch (error) {
        toast.error("Fehler beim Löschen");
      }
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData(item);
    setShowForm(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-heading font-bold text-2xl text-white">Portfolio verwalten</h2>
        <button
          onClick={() => { setShowForm(true); setEditingItem(null); setFormData({ name: "", category_en: "", category_de: "", category_ar: "", logo: "", url: "#" }); }}
          className="inline-flex items-center gap-2 border border-white bg-white text-black px-4 py-2 font-heading font-bold text-sm uppercase tracking-widest hover:bg-black hover:text-white transition-all duration-300"
        >
          <Plus size={16} />
          Neu
        </button>
      </div>

      {showForm && (
        <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6 space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg text-white">{editingItem ? "Portfolio bearbeiten" : "Neues Portfolio"}</h3>
            <button onClick={() => setShowForm(false)} className="text-mmg-silver-chalice hover:text-white"><X size={20} /></button>
          </div>
          
          <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="Name / Brand" className="w-full py-2 px-3 bg-black border border-mmg-mine-shaft text-white font-body" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <input type="text" value={formData.category_en} onChange={(e) => setFormData({...formData, category_en: e.target.value})} placeholder="Category (EN)" className="py-2 px-3 bg-black border border-mmg-mine-shaft text-white font-body" />
            <input type="text" value={formData.category_de} onChange={(e) => setFormData({...formData, category_de: e.target.value})} placeholder="Kategorie (DE)" className="py-2 px-3 bg-black border border-mmg-mine-shaft text-white font-body" />
            <input type="text" value={formData.category_ar} onChange={(e) => setFormData({...formData, category_ar: e.target.value})} placeholder="الفئة (AR)" dir="rtl" className="py-2 px-3 bg-black border border-mmg-mine-shaft text-white font-body" />
          </div>
          <input type="text" value={formData.logo} onChange={(e) => setFormData({...formData, logo: e.target.value})} placeholder="Logo URL (z.B. /misr-vera-logo.png)" className="w-full py-2 px-3 bg-black border border-mmg-mine-shaft text-white font-body" />
          <input type="text" value={formData.url} onChange={(e) => setFormData({...formData, url: e.target.value})} placeholder="Website URL (optional)" className="w-full py-2 px-3 bg-black border border-mmg-mine-shaft text-white font-body" />
          
          <button onClick={handleSave} className="inline-flex items-center gap-2 border border-white bg-white text-black px-4 py-2 font-heading font-bold text-sm uppercase tracking-widest hover:bg-black hover:text-white transition-all duration-300">
            <Save size={16} />
            Speichern
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {portfolio.map((item) => (
          <div key={item.id} className="flex items-center justify-between bg-mmg-cod-gray border border-mmg-mine-shaft p-4 hover:border-mmg-boulder transition-colors">
            <div className="flex items-center gap-4">
              {item.logo && <img src={item.logo} alt={item.name} className="w-12 h-12 object-contain bg-black" />}
              <div>
                <p className="font-heading text-white">{item.name}</p>
                <p className="font-body text-sm text-mmg-silver-chalice">{item.category_de || item.category_en}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => handleEdit(item)} className="p-2 text-mmg-silver-chalice hover:text-white"><Edit2 size={18} /></button>
              <button onClick={() => handleDelete(item.id)} className="p-2 text-mmg-silver-chalice hover:text-red-500"><Trash2 size={18} /></button>
            </div>
          </div>
        ))}
        {portfolio.length === 0 && <p className="text-mmg-silver-chalice text-center py-8 col-span-2">Keine Portfolio-Einträge vorhanden</p>}
      </div>
    </div>
  );
};

// Jobs Tab Component
const JobsTab = ({ jobs, onUpdate }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    title: "", office: "", function: "", type: "", description: "", featured: false
  });

  const handleSave = async () => {
    try {
      if (editingItem) {
        await axios.put(`${API}/admin/jobs/${editingItem.id}`, formData);
        toast.success("Job aktualisiert!");
      } else {
        await axios.post(`${API}/admin/jobs`, formData);
        toast.success("Job erstellt!");
      }
      setShowForm(false);
      setEditingItem(null);
      setFormData({ title: "", office: "", function: "", type: "", description: "", featured: false });
      onUpdate();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Wirklich löschen?")) {
      try {
        await axios.delete(`${API}/admin/jobs/${id}`);
        toast.success("Job gelöscht!");
        onUpdate();
      } catch (error) {
        toast.error("Fehler beim Löschen");
      }
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData(item);
    setShowForm(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-heading font-bold text-2xl text-white">Jobs verwalten</h2>
        <button
          onClick={() => { setShowForm(true); setEditingItem(null); setFormData({ title: "", office: "", function: "", type: "", description: "", featured: false }); }}
          className="inline-flex items-center gap-2 border border-white bg-white text-black px-4 py-2 font-heading font-bold text-sm uppercase tracking-widest hover:bg-black hover:text-white transition-all duration-300"
        >
          <Plus size={16} />
          Neu
        </button>
      </div>

      {showForm && (
        <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6 space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg text-white">{editingItem ? "Job bearbeiten" : "Neuer Job"}</h3>
            <button onClick={() => setShowForm(false)} className="text-mmg-silver-chalice hover:text-white"><X size={20} /></button>
          </div>
          
          <input type="text" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} placeholder="Job Title" className="w-full py-2 px-3 bg-black border border-mmg-mine-shaft text-white font-body" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <input type="text" value={formData.office} onChange={(e) => setFormData({...formData, office: e.target.value})} placeholder="Office (z.B. Mettingen, Remote)" className="py-2 px-3 bg-black border border-mmg-mine-shaft text-white font-body" />
            <input type="text" value={formData.function} onChange={(e) => setFormData({...formData, function: e.target.value})} placeholder="Function (z.B. Marketing)" className="py-2 px-3 bg-black border border-mmg-mine-shaft text-white font-body" />
            <input type="text" value={formData.type} onChange={(e) => setFormData({...formData, type: e.target.value})} placeholder="Type (z.B. Full-time)" className="py-2 px-3 bg-black border border-mmg-mine-shaft text-white font-body" />
          </div>
          <textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} placeholder="Beschreibung" rows={3} className="w-full py-2 px-3 bg-black border border-mmg-mine-shaft text-white font-body resize-none" />
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={formData.featured} onChange={(e) => setFormData({...formData, featured: e.target.checked})} className="w-5 h-5" />
            <span className="font-body text-white">Featured Job</span>
          </label>
          
          <button onClick={handleSave} className="inline-flex items-center gap-2 border border-white bg-white text-black px-4 py-2 font-heading font-bold text-sm uppercase tracking-widest hover:bg-black hover:text-white transition-all duration-300">
            <Save size={16} />
            Speichern
          </button>
        </div>
      )}

      <div className="space-y-2">
        {jobs.map((job) => (
          <div key={job.id} className="flex items-center justify-between bg-mmg-cod-gray border border-mmg-mine-shaft p-4 hover:border-mmg-boulder transition-colors">
            <div>
              <div className="flex items-center gap-2">
                <p className="font-heading text-white">{job.title}</p>
                {job.featured && <span className="px-2 py-0.5 bg-white text-black text-xs font-heading uppercase">Featured</span>}
              </div>
              <p className="font-body text-sm text-mmg-silver-chalice">{job.office} • {job.function} • {job.type}</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => handleEdit(job)} className="p-2 text-mmg-silver-chalice hover:text-white"><Edit2 size={18} /></button>
              <button onClick={() => handleDelete(job.id)} className="p-2 text-mmg-silver-chalice hover:text-red-500"><Trash2 size={18} /></button>
            </div>
          </div>
        ))}
        {jobs.length === 0 && <p className="text-mmg-silver-chalice text-center py-8">Keine Jobs vorhanden</p>}
      </div>
    </div>
  );
};

// Social Media Tab Component
const SocialTab = ({ links, onUpdate }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ platform: "", url: "", enabled: true });

  const platformOptions = ["instagram", "linkedin", "twitter", "facebook", "youtube", "tiktok"];

  const handleSave = async () => {
    try {
      if (editingItem) {
        await axios.put(`${API}/admin/social/${editingItem.id}`, formData);
        toast.success("Social Link aktualisiert!");
      } else {
        await axios.post(`${API}/admin/social`, formData);
        toast.success("Social Link erstellt!");
      }
      setShowForm(false);
      setEditingItem(null);
      setFormData({ platform: "", url: "", enabled: true });
      onUpdate();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Wirklich löschen?")) {
      try {
        await axios.delete(`${API}/admin/social/${id}`);
        toast.success("Social Link gelöscht!");
        onUpdate();
      } catch (error) {
        toast.error("Fehler beim Löschen");
      }
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData(item);
    setShowForm(true);
  };

  const toggleEnabled = async (item) => {
    try {
      await axios.put(`${API}/admin/social/${item.id}`, { ...item, enabled: !item.enabled });
      toast.success(item.enabled ? "Deaktiviert" : "Aktiviert");
      onUpdate();
    } catch (error) {
      toast.error("Fehler");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-heading font-bold text-2xl text-white">Social Media verwalten</h2>
        <button
          onClick={() => { setShowForm(true); setEditingItem(null); setFormData({ platform: "", url: "", enabled: true }); }}
          className="inline-flex items-center gap-2 border border-white bg-white text-black px-4 py-2 font-heading font-bold text-sm uppercase tracking-widest hover:bg-black hover:text-white transition-all duration-300"
        >
          <Plus size={16} />
          Neu
        </button>
      </div>

      {showForm && (
        <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6 space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg text-white">{editingItem ? "Social Link bearbeiten" : "Neuer Social Link"}</h3>
            <button onClick={() => setShowForm(false)} className="text-mmg-silver-chalice hover:text-white"><X size={20} /></button>
          </div>
          
          <select value={formData.platform} onChange={(e) => setFormData({...formData, platform: e.target.value})} className="w-full py-2 px-3 bg-black border border-mmg-mine-shaft text-white font-body">
            <option value="">Platform wählen...</option>
            {platformOptions.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
          </select>
          <input type="text" value={formData.url} onChange={(e) => setFormData({...formData, url: e.target.value})} placeholder="URL (z.B. https://instagram.com/...)" className="w-full py-2 px-3 bg-black border border-mmg-mine-shaft text-white font-body" />
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={formData.enabled} onChange={(e) => setFormData({...formData, enabled: e.target.checked})} className="w-5 h-5" />
            <span className="font-body text-white">Aktiviert (sichtbar auf Website)</span>
          </label>
          
          <button onClick={handleSave} className="inline-flex items-center gap-2 border border-white bg-white text-black px-4 py-2 font-heading font-bold text-sm uppercase tracking-widest hover:bg-black hover:text-white transition-all duration-300">
            <Save size={16} />
            Speichern
          </button>
        </div>
      )}

      <div className="space-y-2">
        {links.map((link) => (
          <div key={link.id} className="flex items-center justify-between bg-mmg-cod-gray border border-mmg-mine-shaft p-4 hover:border-mmg-boulder transition-colors">
            <div className="flex items-center gap-4">
              <div className={`w-10 h-10 flex items-center justify-center border ${link.enabled ? 'border-white text-white' : 'border-mmg-boulder text-mmg-boulder'}`}>
                <Share2 size={18} />
              </div>
              <div>
                <p className={`font-heading ${link.enabled ? 'text-white' : 'text-mmg-boulder'}`}>{link.platform}</p>
                <p className="font-body text-sm text-mmg-silver-chalice truncate max-w-xs">{link.url}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => toggleEnabled(link)} className="p-2 text-mmg-silver-chalice hover:text-white">
                {link.enabled ? <Eye size={18} /> : <EyeOff size={18} />}
              </button>
              <button onClick={() => handleEdit(link)} className="p-2 text-mmg-silver-chalice hover:text-white"><Edit2 size={18} /></button>
              <button onClick={() => handleDelete(link.id)} className="p-2 text-mmg-silver-chalice hover:text-red-500"><Trash2 size={18} /></button>
            </div>
          </div>
        ))}
        {links.length === 0 && <p className="text-mmg-silver-chalice text-center py-8">Keine Social Links vorhanden</p>}
      </div>
    </div>
  );
};

// Contact Messages Tab Component
const ContactTab = ({ messages, onUpdate }) => {
  const [selectedMessage, setSelectedMessage] = useState(null);

  const handleDelete = async (id) => {
    if (window.confirm("Wirklich löschen?")) {
      try {
        await axios.delete(`${API}/admin/contact/${id}`);
        toast.success("Nachricht gelöscht!");
        setSelectedMessage(null);
        onUpdate();
      } catch (error) {
        toast.error("Fehler beim Löschen");
      }
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="font-heading font-bold text-2xl text-white">Kontaktanfragen ({messages.length})</h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Messages List */}
        <div className="space-y-2 max-h-[600px] overflow-y-auto">
          {messages.map((msg) => (
            <button
              key={msg.id}
              onClick={() => setSelectedMessage(msg)}
              className={`w-full text-left p-4 border transition-colors ${
                selectedMessage?.id === msg.id 
                  ? 'bg-mmg-cod-gray border-white' 
                  : 'bg-mmg-cod-gray border-mmg-mine-shaft hover:border-mmg-boulder'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <p className="font-heading text-white">{msg.firstName} {msg.lastName}</p>
                <p className="font-body text-xs text-mmg-boulder">{new Date(msg.created_at).toLocaleDateString('de-DE')}</p>
              </div>
              <p className="font-body text-sm text-mmg-silver-chalice">{msg.email}</p>
              <p className="font-body text-sm text-mmg-boulder truncate">{msg.message || "Keine Nachricht"}</p>
            </button>
          ))}
          {messages.length === 0 && <p className="text-mmg-silver-chalice text-center py-8">Keine Kontaktanfragen</p>}
        </div>

        {/* Message Detail */}
        {selectedMessage && (
          <div className="bg-mmg-cod-gray border border-mmg-mine-shaft p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-lg text-white">Nachricht Details</h3>
              <button onClick={() => setSelectedMessage(null)} className="text-mmg-silver-chalice hover:text-white"><X size={20} /></button>
            </div>
            
            <div className="space-y-4">
              <div>
                <p className="font-heading text-xs text-mmg-silver-chalice uppercase tracking-widest mb-1">Name</p>
                <p className="font-body text-white">{selectedMessage.firstName} {selectedMessage.lastName}</p>
              </div>
              <div>
                <p className="font-heading text-xs text-mmg-silver-chalice uppercase tracking-widest mb-1">E-Mail</p>
                <a href={`mailto:${selectedMessage.email}`} className="font-body text-white hover:underline">{selectedMessage.email}</a>
              </div>
              {selectedMessage.phone && (
                <div>
                  <p className="font-heading text-xs text-mmg-silver-chalice uppercase tracking-widest mb-1">Telefon</p>
                  <p className="font-body text-white">{selectedMessage.phone}</p>
                </div>
              )}
              {selectedMessage.company && (
                <div>
                  <p className="font-heading text-xs text-mmg-silver-chalice uppercase tracking-widest mb-1">Unternehmen</p>
                  <p className="font-body text-white">{selectedMessage.company}</p>
                </div>
              )}
              <div>
                <p className="font-heading text-xs text-mmg-silver-chalice uppercase tracking-widest mb-1">Standort</p>
                <p className="font-body text-white">{selectedMessage.city || "-"}, {selectedMessage.country}</p>
              </div>
              <div>
                <p className="font-heading text-xs text-mmg-silver-chalice uppercase tracking-widest mb-1">Nachricht</p>
                <p className="font-body text-white whitespace-pre-wrap">{selectedMessage.message || "Keine Nachricht"}</p>
              </div>
              <div>
                <p className="font-heading text-xs text-mmg-silver-chalice uppercase tracking-widest mb-1">Eingegangen</p>
                <p className="font-body text-white">{new Date(selectedMessage.created_at).toLocaleString('de-DE')}</p>
              </div>
            </div>

            <div className="flex gap-4 mt-8">
              <a
                href={`mailto:${selectedMessage.email}?subject=Re: Ihre Anfrage bei Mahzod Media Group`}
                className="inline-flex items-center gap-2 border border-white bg-white text-black px-4 py-2 font-heading font-bold text-sm uppercase tracking-widest hover:bg-black hover:text-white transition-all duration-300"
              >
                <Mail size={16} />
                Antworten
              </a>
              <button
                onClick={() => handleDelete(selectedMessage.id)}
                className="inline-flex items-center gap-2 border border-red-500 text-red-500 px-4 py-2 font-heading font-bold text-sm uppercase tracking-widest hover:bg-red-500 hover:text-white transition-all duration-300"
              >
                <Trash2 size={16} />
                Löschen
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
